// Human-friendly explanations for common Meta (Facebook / Instagram) publish
// errors. The RAW error string returned from the backend / Meta Graph API is
// ALWAYS preserved in Delivery Results (inside PostEditorDialog). This module
// is used purely for the card-level surface where a customer needs an
// immediately actionable next step without opening the editor.
//
// If a raw error doesn't match any known pattern we return the raw string
// unchanged, so nothing is silently swallowed. Patterns are matched
// case-insensitively via substring / regex.
//
// Add new mappings here as Meta surfaces new user-facing errors. Do NOT
// rewrite or hide the raw error server-side.

/**
 * @typedef {Object} FriendlyError
 * @property {string} title       Short heading, e.g. "Facebook needs identity confirmation".
 * @property {string} explanation Full user-facing sentence(s) with next steps.
 * @property {string} [actionLabel] Optional label for a primary action button.
 * @property {boolean} matched    True when a mapping matched; false when we fell back to the raw error.
 */

const PATTERNS = [
  {
    // Facebook Page identity confirmation (Meta's step-up verification flow).
    // Copy sourced from the user-reported production error.
    test: /confirm your identity before you can publish as this page/i,
    build: (platform) => ({
      title: `${platform} requires identity confirmation`,
      explanation:
        `${platform} requires you to confirm your identity before publishing as this Page. ` +
        `Open the Facebook mobile app using the account that manages this Page, complete ` +
        `Facebook's instructions, then return here and click Retry ${platform}.`,
      actionLabel: `Retry ${platform}`,
    }),
  },
  {
    // Access-token issues (expired, revoked, malformed).
    test: /(invalid oauth access token|error validating access token|session has expired|session has been invalidated|the user has not authorized application|access token.*(expired|invalid|revoked)|token.*(expired|invalid|revoked))/i,
    build: (platform) => ({
      title: `Reconnect ${platform}`,
      explanation:
        `Your ${platform} connection has expired. Reconnect ${platform} in ` +
        `Connections and then click Retry ${platform} to finish publishing this post.`,
      actionLabel: "Go to Connections",
    }),
  },
  {
    // Missing permission scope (pages_manage_posts / instagram_content_publish etc.)
    test: /(does not have permission|missing permissions|permissions error|requires (?:the )?scope|requires .*_content_publish|requires .*_manage_posts)/i,
    build: (platform) => ({
      title: `${platform} is missing a required permission`,
      explanation:
        `${platform} rejected the publish because a required permission wasn't granted. ` +
        `Reconnect ${platform} in Connections and approve every permission that Meta asks ` +
        `for, then click Retry ${platform}.`,
      actionLabel: "Go to Connections",
    }),
  },
  {
    // ASPECT RATIO — Instagram feed images must be between 4:5 and 1.91:1.
    // Meta returns messages like "The aspect ratio is not supported" often
    // WRAPPED inside "Instagram media container create failed: …", so this
    // pattern MUST run BEFORE the generic media-container / fetch pattern
    // below or the wrong (HTTPS-fetch) explanation wins.
    test: /aspect ratio is not supported|aspect ratio (?:must|should) be|unsupported aspect ratio|image ratio (?:not|is not) supported/i,
    build: (platform) => ({
      title: `${platform} image aspect ratio not supported`,
      explanation:
        `${platform} can't publish this image because its aspect ratio is outside ` +
        `${platform}'s supported range. ${platform} feed images must be between 4:5 ` +
        `and 1.91:1. Resize, crop, or replace the image, then retry.`,
      actionLabel: `Retry ${platform}`,
    }),
  },
  {
    // FILE SIZE — Instagram photo uploads cap at 8 MB (feed) / 30 MB (reels).
    // Meta returns wording like "file size too large" or "exceeds maximum".
    test: /(file (?:is )?too large|file size (?:is )?too large|exceeds (?:the )?(?:maximum|allowed) (?:file )?size|image (?:is )?too (?:big|large)|payload too large|image_size|media_size.*too)/i,
    build: (platform) => ({
      title: `${platform} image is too large`,
      explanation:
        `${platform} rejected this image because the file is larger than the platform ` +
        `allows. Replace it with a smaller or compressed image (under 8 MB for a feed ` +
        `photo), then retry.`,
      actionLabel: `Retry ${platform}`,
    }),
  },
  {
    // UNSUPPORTED MEDIA TYPE — Meta occasionally returns
    // "Unsupported get request" / "media type is not supported" / MIME issues.
    test: /(unsupported (?:media|image|file|content) (?:type|format)|invalid media (?:type|format)|(?:media|image) format (?:is )?not supported|only jpeg|only jpg|invalid file format)/i,
    build: (platform) => ({
      title: `${platform} doesn't support this media format`,
      explanation:
        `${platform} rejected this file because its format isn't supported for an ` +
        `${platform} post. Replace it with a supported image (JPEG or PNG), then retry.`,
      actionLabel: `Retry ${platform}`,
    }),
  },
  {
    // MISSING CONNECTION — Postmoro-side "Instagram not connected" surface.
    // Emitted by the backend when there's no social_connections row for this
    // workspace.
    test: /(no (?:instagram|facebook|linkedin|x|twitter) connection|not connected (?:to|for) (?:instagram|facebook|linkedin|x|twitter)|(?:instagram|facebook|linkedin|x|twitter) is not connected|missing (?:instagram|facebook|linkedin|x|twitter) connection|no social connection)/i,
    build: (platform) => ({
      title: `${platform} is not connected`,
      explanation:
        `Connect ${platform} for this business workspace before publishing. Open ` +
        `Connections, sign in to ${platform}, then click Retry ${platform}.`,
      actionLabel: "Go to Connections",
    }),
  },
  {
    // Image URL not fetchable — Meta could not DOWNLOAD/ACCESS the image
    // itself. Narrower than before so aspect-ratio, size, and format
    // failures no longer accidentally match this "could not fetch" copy.
    test: /(image[_ ]url.*(?:not (?:reachable|accessible)|could not be (?:reached|fetched|downloaded)|is not (?:https|public))|could not (?:fetch|download|process) the image url|url must be publicly accessible|image[_ ]url is (?:invalid|inaccessible|not reachable)|fetch(?:ing)? image (?:failed|timed out))/i,
    build: (platform) => ({
      title: `${platform} could not access the image`,
      explanation:
        `${platform} could not fetch the image attached to this post. Make sure the ` +
        `image is set, under 8MB, and publicly accessible over HTTPS, then click ` +
        `Retry ${platform}.`,
      actionLabel: `Retry ${platform}`,
    }),
  },
  {
    // Instagram-only: text-only posts are not allowed.
    test: /instagram requires an image|no image attached/i,
    build: (platform) => ({
      title: `${platform} requires an image`,
      explanation:
        `${platform} does not allow text-only posts. Add an image to this post, then click ` +
        `Retry ${platform}.`,
      actionLabel: `Retry ${platform}`,
    }),
  },
  {
    // No linked Instagram Business account discovered on the selected Page.
    test: /no linked instagram business account|no linked ig|linked instagram business or creator account/i,
    build: (platform) => ({
      title: `No linked ${platform} Business account`,
      explanation:
        `The Facebook Page you selected in Connections is not linked to an ${platform} ` +
        `Business or Creator account. Link them inside the Facebook app or Meta Business ` +
        `Suite, then click Retry ${platform}.`,
      actionLabel: "Go to Connections",
    }),
  },
  {
    // Rate limited by Meta.
    test: /rate limit|(?:too many|excessive) (?:calls|requests)|application request limit reached/i,
    build: (platform) => ({
      title: `${platform} rate limit reached`,
      explanation:
        `Meta is temporarily rate-limiting publish requests from Postmoro. Wait a few ` +
        `minutes and then click Retry ${platform}.`,
      actionLabel: `Retry ${platform}`,
    }),
  },
  {
    // Duplicate content flagged by Meta.
    test: /duplicate (?:post|content|status)|same content was recently posted/i,
    build: (platform) => ({
      title: `${platform} flagged this as duplicate`,
      explanation:
        `${platform} rejected the post because identical content was published recently. ` +
        `Edit the caption or image slightly and then click Retry ${platform}.`,
      actionLabel: `Retry ${platform}`,
    }),
  },
];

/**
 * Map a raw platform publish error string to a friendly, actionable
 * explanation for the given platform name.
 *
 * @param {string} platform     The platform name (e.g. "Facebook", "Instagram").
 * @param {string|null} rawError The raw error message from the backend / Meta.
 * @returns {FriendlyError}
 */
export function toFriendlyPublishError(platform, rawError) {
  const raw = (rawError || "").trim();
  const platformLabel = platform || "Platform";
  if (!raw) {
    return {
      title: `${platformLabel} failed to publish`,
      explanation:
        `${platformLabel} returned an error while publishing this post. Review the ` +
        `error details and try again.`,
      actionLabel: `Retry ${platformLabel}`,
      matched: false,
    };
  }
  for (const p of PATTERNS) {
    if (p.test.test(raw)) {
      return { ...p.build(platformLabel), matched: true };
    }
  }
  // Fallback: keep the neutral generic explanation per product spec —
  // do NOT invent a specific diagnosis. Raw provider text is still
  // surfaced verbatim inside PostEditorDialog's technical detail
  // areas, so nothing is hidden from developers/admins.
  return {
    title: `${platformLabel} failed to publish`,
    explanation:
      `${platformLabel} returned an error while publishing this post. Review the ` +
      `error details and try again.`,
    actionLabel: `Retry ${platformLabel}`,
    matched: false,
  };
}
